<script setup>
import {onMounted, ref, watch, computed} from "vue";
import {GoogleMap} from "vue3-google-map";
import {useField} from "vee-validate";
import useDebouncedRef from '~/composables/useDebouncedRef'


const mapRef = ref(null);
const searchInput = ref(null);
const searchCity = ref(null);
const verifyStatus = ref(true);
let autocompleteService = null;
let cityComplete = null;

const props = defineProps({
  placeholder: {
    type: String,
    default: '',
  },
  address: {
    type: String,
    default: '',
  },
  city: {
    type: String,
    default: '',
  },
  state: {
    type: String,
    default: '',
  },
  country: {
    type: String,
    default: '',
  },
  zip_code: {
    type: String,
    default: '',
  },
  messageZip_code: {
    type: String,
    default: '',
  },
  state_message: {
    type: String,
    default: '',
  },
  country_options: {
    type: Array,
    default: null
  },
  state_options: {
    type: Array,
    default: null
  },
  name:{
    type: String,
    default: 'name',
  },
  city_name:{
    type: String,
    default: 'city_name',
  },
  upperText: Boolean,
  disabled: Boolean,
  withCity: Boolean,
  borderless: Boolean,
  borderbottom: Boolean,
  borderradiusnone: Boolean,
  required: Boolean,
  front: Boolean,
});

const emit = defineEmits(['update:address', 'update:zip_code', 'update:messageZip_code', 'update:city', 'update:state', 'update:state_message', 'update:country']);

const watchAddress = useDebouncedRef(props.address,1000);
const watchCity = useDebouncedRef(props.city,1000);
const watchZip = useDebouncedRef(props.zip_code,800);

const {
  Value: value,
  errorMessage,
  meta,
} =  useField(props.withCity ? props.city_name : props.name, undefined, {
  initialValue: props.withCity ? props.city : props.address,
});

const zip_code_message = computed({
  get: () => props.messageZip_code,
  set: value => {
    emit('update:messageZip_code', value)
  }
});

const message = computed({
  get: () => props.messageZip_code,
  set: value => {
    emit('update:state_message', value)
  }
});

const computedAddress = computed({
  get: () => props.address,
  set: value => {
    emit('update:address', value)
  }
});

const computedCity = computed({
  get: () => props.city,
  set: value => {
    emit('update:city', value)
  }
});

const computedCountry = computed({
  get: () => props.country,
  set: value => {
    emit('update:country', value)
  }
});

const computedZip_code = computed({
  get: () => props.zip_code,
  set: value => {
    emit('update:zip_code', value)
  }
});

const computedState = computed({
  get: () => props.state,
  set: value => {
    emit('update:state', value)
  }
});

const states = computed(() => props.state_options)

const inputElClass = computed(() => {
  const base = [
    'px-3 py-2 max-w-full focus:ring focus:outline-none border-gray-700 rounded w-full text-xs',
    'dark:placeholder-gray-400',
    'h-10',
    'border',
    'bg-white dark:bg-gray-800',
    props.disabled ? 'bg-gray-200' : '',
    props.borderless ? 'border-0' : 'border',
    props.borderbottom ? 'border-b border-stone-900' : 'border',
    props.borderradiusnone ? 'rounded-none' : '',
  ]

  if (props.icon) {
    base.push('pl-10')
  }

  return base
})

const verifyAddress = () => {
  if(computedCountry.value === 1 || computedCountry.value === 'US') {
    let verify = new mapRef.value.api.Geocoder();
    let address_components = [];
    if(computedAddress.value && computedCity.value && verifyStatus.value) {
      verifyStatus.value = false;
      message.value = '';
      zip_code_message.value = '';
      verify.geocode({address: computedAddress.value, componentRestrictions: {locality: computedCity.value, administrativeArea:computedState.value}}, function (results, status) {
        if (status === mapRef.value.api.GeocoderStatus.OK) {
          address_components = results[0].address_components;
          if(address_components.length <= 6){
            message.value = 'Invalid Address/City/State combination!';
          }
          else{
                address_components.forEach(component => {
                  component.types.forEach(type => {
                    if ((type === 'locality' || type === 'sublocality') && computedCity.value.toLowerCase() !== component.long_name.toLowerCase()) {
                      message.value = 'Invalid Address/City/State combination!';
                    }
                    if (type === 'locality' && computedCity.value.toLowerCase() === component.long_name.toLowerCase()){
                      message.value = '';
                    }
                    if (type === 'administrative_area_level_1' && computedState.value.toLowerCase() !== component.short_name.toLowerCase()) {
                      message.value = 'Invalid Address/City/State combination!';
                    }
                    if (type === 'postal_code' && component.long_name !== computedZip_code.value) {
                      zip_code_message.value = 'Invalid Zip Code for Address/City/State Combination! Correct Zip Code: ' + component.long_name;
                    }
                    if(type === 'postal_code' && component.long_name === computedZip_code.value){
                      zip_code_message.value = '';
                    }
                  })
                })
          }

        }else if(status === 'ZERO_RESULTS'){
          message.value = 'Invalid Address/City/State combination!';
        }
      })
    }
    else if(computedCity.value && computedAddress.value === '' && verifyStatus.value){
      verifyStatus.value = false;
      verify.geocode({address: computedCity.value, componentRestrictions: {administrativeArea:computedState.value}}, function (results, status) {
        if (status === mapRef.value.api.GeocoderStatus.OK) {
          address_components = results[0].address_components;
          message.value = '';
          zip_code_message.value = '';
          address_components.forEach(component => {
            component.types.forEach(type => {
              if ((type === 'locality' || type === 'sublocality') && computedCity.value.toLowerCase() !== component.long_name.toLowerCase()) {
                message.value = 'Invalid Address/City/State combination!';
              }
              if (type === 'locality' && computedCity.value === component.long_name){
                message.value = '';
              }
              if (type === 'administrative_area_level_1' && computedState.value.toLowerCase() !== component.short_name.toLowerCase()) {
                message.value = 'Invalid Address/City/State combination!';
              }
              if(computedZip_code.value){
                zip_code_message.value = 'Invalid Zip Code for Address/City/State Combination!';
              }
            })
          })
        }else if(status === 'ZERO_RESULTS'){
          message.value = 'Invalid Address/City/State combination!';
        }
      })
    }
    else if(computedAddress.value && verifyStatus.value){
        verifyStatus.value = false;
        message.value = '';
        zip_code_message.value = '';
        verify.geocode({address: computedAddress.value}, function (results, status) {
          if (status === mapRef.value.api.GeocoderStatus.OK) {
            address_components = results[0].address_components;
            message.value = '';
            zip_code_message.value = '';
            address_components.forEach(component => {
              component.types.forEach(type => {
                if ((type === 'locality' || type === 'sublocality') && computedCity.value.toLowerCase() !== component.long_name.toLowerCase()) {
                  message.value = 'Invalid Address/City/State combination!';
                }
                if (type === 'locality' && computedCity.value.toLowerCase() === component.long_name.toLowerCase()){
                  message.value = '';
                }
                if (type === 'administrative_area_level_1' && computedState.value.toLowerCase() !== component.short_name.toLowerCase()) {
                  message.value = 'Invalid Address/City/State combination!';
                }
                if (type === 'postal_code' && component.long_name !== computedZip_code.value) {
                  zip_code_message.value = 'Invalid Zip Code for Address/City/State Combination! Correct Zip Code: ' + component.long_name;
                }
                else{
                  zip_code_message.value = '';
                }
              })
            })
          }else if(status === 'ZERO_RESULTS'){
            message.value = 'Invalid Address/City/State combination!';
          }
        })
      }
    setTimeout(()=>{
      verifyStatus.value = true;
    },700);
  }
  else{
    message.value = '';
    zip_code_message.value = '';
  }
}

onMounted(() => {
  watch(() => mapRef.value?.ready, (ready) => {
    if(props.withCity){
      let geocoder = new mapRef.value.api.Geocoder();
      let options = {types: ['(cities)'], componentRestrictions: {country: 'us'}};
      cityComplete = new mapRef.value.api.places.Autocomplete(searchCity.value, options)
      cityComplete.setComponentRestrictions({
        country: ["us"],
      });
      cityComplete.addListener('place_changed', () => {
        let place = cityComplete.getPlace();
        let address_components = [];
        geocoder.geocode({address: place.formatted_address}, function (results, status) {
          if (status === mapRef.value.api.GeocoderStatus.OK) {
            address_components = results[0].address_components;
            computedCity.value = '';
            computedState.value = '';
            computedCountry.value = '';
            message.value = '';
            zip_code_message.value = '';
            address_components.forEach(component => {
              component.types.forEach(type => {
                if (type === 'locality' || type === 'sublocality') {
                  computedCity.value = component.long_name;
                }
                if (type === 'administrative_area_level_1') {
                  computedState.value = component.short_name;
                }
                if (type === 'country') {
                  let i = props.country_options.map(item => item.id).indexOf(component.short_name);
                  computedCountry.value = props.country_options[i].id;
                }
              })
            })
          }
        })
      })
    }else {
      let geocoder = new mapRef.value.api.Geocoder();
      autocompleteService = new mapRef.value.api.places.Autocomplete(searchInput.value)
      autocompleteService.setComponentRestrictions({
        country: ["us"],
      });
      autocompleteService.addListener('place_changed', () => {
        let place = autocompleteService.getPlace();
        let address_components = [];
        geocoder.geocode({address: place.formatted_address}, function (results, status) {
          if (status === mapRef.value.api.GeocoderStatus.OK) {
            address_components = results[0].address_components;
            let Address = '';
            let City = '';
            let State = '';
            let Country = '';
            let Zip_code = '';
            address_components.forEach(component => {
              component.types.forEach(type => {
                if (type === 'street_number') {
                  Address = component.long_name;
                }
                if (type === 'route') {
                  Address = Address + ' ' + component.long_name;
                }
                if (type === 'locality' || type === 'sublocality') {
                  City = component.long_name;
                }
                if (type === 'administrative_area_level_1') {
                  State = component.short_name;
                }
                if (type === 'country') {
                  let i = props.country_options.map(item => item.id.toLowerCase()).indexOf(component.short_name.toLowerCase());
                  Country = props.country_options[i].id;
                }
                if (type === 'postal_code') {
                  Zip_code = component.long_name;
                }
              })
            })
            computedAddress.value = Address;
            computedCity.value = City;
            computedState.value = State;
            computedCountry.value = Country;
            computedZip_code.value = Zip_code;
          }
        })
      });
    }
  })

  watch(() => watchAddress.value, (newValue, oldValue) =>{
    if(newValue !== oldValue){
      verifyAddress();
    }
  })

  watch(() => computedZip_code.value, (newValue, oldValue) =>{
    if(newValue !== oldValue){
      watchZip.value = computedZip_code.value;
    }
  })
  watch(() => watchZip.value, (newValue, oldValue) =>{
    if(newValue !== oldValue){
      verifyAddress();
    }
  })
  watch(()=> computedState.value, (newValue, oldValue) =>{
    if(newValue != null && newValue !== oldValue){
      verifyAddress();
    }
  })
  watch(()=> computedCity.value, (newValue, oldValue) =>{
    if(newValue !== '' && newValue !== oldValue){
      watchCity.value = computedCity.value;
    }
  })
  watch(()=> watchCity.value, (newValue, oldValue) =>{
    if(newValue !== '' && newValue !== oldValue){
      verifyAddress();
    }
  })

  watch(()=> computedCountry.value, (newValue, oldValue) =>{
    verifyAddress();
  })
})
</script>

<template>
  <div>
    <GoogleMap
        ref="mapRef"
        api-key="AIzaSyBXI-L1CBAcZkeVCIxtDXmu_tMtqdXV3wY"
    >
      <template #default="{ ready, api, map, mapTilesLoaded }">
      </template>
    </GoogleMap>
    <div>
      <template v-if="!front">
        <admin-field
            :label="required? 'City *:':'City :'"
            :upperText="upperText"
            v-if="withCity"
        >
          <div>
            <V-Field
                :name="city_name"
                v-slot="{ field }"
                v-model="computedCity"
                :disabled="disabled"
            >
              <input
                  v-bind="field"
                  ref="searchCity"
                  :class="inputElClass"
                  type="text"
                  :name="city_name"
                  v-model="watchCity"
                  :disabled="disabled"
                  :placeholder="placeholder"
                  borderbottom
                  borderless
                  borderradiusnone
              />
            </V-Field>
            <p class="text-red-600" v-show="(errorMessage || meta.valid)">
              {{ errorMessage }}
            </p>
          </div>
        </admin-field>
        <admin-field
            :label="required? 'Address *:':'Address :'"
            :upperText="upperText"
            v-else
        >
          <div>
            <V-Field
                :name="name"
                v-slot="{ field }"
                v-model="computedAddress"
                :disabled="disabled"
            >
              <input
                  v-bind="field"
                  ref="searchInput"
                  :class="inputElClass"
                  type="text"
                  :name="name"
                  v-model="watchAddress"
                  :disabled="disabled"
                  :placeholder="placeholder"
              />
            </V-Field>
            <p class="text-red-600" v-show="(errorMessage || meta.valid)">
              {{ errorMessage }}
            </p>
          </div>
        </admin-field>
      </template>
      <template v-else>
        <front-field
            :label="required? 'City *:':'City :'"
            :upperText="upperText"
            v-if="withCity"
        >
          <div>
            <V-Field
                :name="city_name"
                v-slot="{ field }"
                v-model="computedCity"
                :disabled="disabled"
            >
              <input
                  v-bind="field"
                  ref="searchCity"
                  :class="inputElClass"
                  type="text"
                  :name="city_name"
                  v-model="watchCity"
                  :disabled="disabled"
                  :placeholder="placeholder"
              />
            </V-Field>
            <p class="text-red-600" v-show="(errorMessage || meta.valid)">
              {{ errorMessage }}
            </p>
          </div>
        </front-field>
        <front-field
            :label="required? 'Address *:':'Address :'"
            :upperText="upperText"
            v-else
        >
          <div >
            <V-Field
                :name="name"
                v-slot="{ field }"
                v-model="computedAddress"
                :disabled="disabled"

            >
              <input
                  v-bind="field"
                  ref="searchInput"
                  :class="inputElClass"
                  type="text"
                  :name="name"
                  v-model="watchAddress"
                  :disabled="disabled"
                  :placeholder="placeholder"
              />
            </V-Field>
            <p class="text-red-600" v-show="(errorMessage || meta.valid)">
              {{ errorMessage }}
            </p>
          </div>
        </front-field>
      </template>
    </div>
  </div>
</template>
